import { Popover, Tooltip } from "@mantine/core";
import IconBell from "@src/assets/icons/lucide/bell.svg?react";
import clsx from "clsx";
import { useState } from "react";
import { useMentionUnreadCount } from "../queries/mention-inbox";
import { useMentionInboxSubscription } from "../subscriptions/on-mention-inbox-updated";
import { MentionInboxPopover } from "./mention-inbox-popover";

export function MentionInboxBell(props: {
	className?: string;
	size?: "xs" | "sm" | "md" | "lg";
	variant?: "header" | "navbar";
}) {
	const { className, size = "md", variant = "navbar" } = props;
	const [opened, setOpened] = useState(false);

	const { data: unreadCount = 0 } = useMentionUnreadCount();
	useMentionInboxSubscription();

	const hasUnread = unreadCount > 0;

	return (
		<Tooltip
			disabled={opened}
			label={hasUnread ? `${unreadCount} unread mentions` : "Mentions inbox"}
			withArrow
			zIndex={9999}
		>
			<Popover
				opened={opened}
				onChange={setOpened}
				position={variant === "navbar" ? "right-start" : "bottom-end"}
				shadow="xl"
				transitionProps={{
					transition: "pop-top-right",
					duration: 150,
				}}
				withArrow={false}
				withinPortal
				zIndex={9999}
			>
				<Popover.Target>
					<button
						type="button"
						aria-label="Mentions inbox"
						onClick={() => setOpened((o) => !o)}
						className={clsx(
							"relative flex shrink-0 items-center justify-center",
							"rounded-lg leading-none cursor-pointer",
							"transition-colors select-none",
							variant === "navbar"
								? clsx(
										"size-6 text-text-secondary",
										"hover:text-text-primary",
										"hover:bg-surface-hover-subtle",
									)
								: clsx(
										"size-8.5 bg-surface-default",
										"hover:bg-surface-hover-subtle",
										"text-text-primary",
										"border border-border-default/60",
									),
							size === "xs" && "size-6",
							size === "sm" && "size-7",
							size === "lg" && "size-10",
							className,
						)}
					>
						<IconBell
							className={clsx(
								size === "xs"
									? "size-3.5"
									: size === "sm"
										? "size-4"
										: size === "lg"
											? "size-5"
											: "size-4.5",
								hasUnread && "text-brand-primary",
							)}
						/>
						{hasUnread && (
							<span
								aria-hidden
								className={clsx(
									"absolute flex items-center justify-center",
									"font-bold text-white rounded-full bg-danger",
									unreadCount > 9
										? clsx(
												"-top-1 -right-1 px-1 min-w-3.5",
												"h-3.5 text-[9px] leading-none",
												"ring-1.5 ring-surface-default",
											)
										: clsx(
												"-top-0.5 -right-0.5 size-3.5",
												"text-[9px] leading-none",
												"ring-1.5 ring-surface-default",
											),
								)}
							>
								{unreadCount > 99 ? "99+" : unreadCount}
							</span>
						)}
					</button>
				</Popover.Target>

				<Popover.Dropdown className="p-0 border-0 bg-transparent shadow-none">
					<MentionInboxPopover onClose={() => setOpened(false)} />
				</Popover.Dropdown>
			</Popover>
		</Tooltip>
	);
}
